import { cn } from '@/lib/utils';

const fmtHours = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

export interface StackBarSegment {
  /** Stable identity for the segment (used as React key). */
  id: string;
  /** Display label inside the legend row. */
  label: string;
  /** Fill color for this segment. */
  color: string;
  /** Minutes this segment contributes — drives both width and the
   *  legend's "5h · 12%" sub-text. Zero-minute segments are skipped
   *  (no zero-width slivers). */
  minutes: number;
}

export interface StackBarProps {
  segments: ReadonlyArray<StackBarSegment>;
  className?: string;
}

/**
 * Reports redesign — workspace project mix. Pill-shaped horizontal
 * stacked bar with a legend below. Each segment's width is
 * proportional to its share of the total. Used at the top of the
 * by-project section to give a one-glance breakdown.
 *
 * Renders nothing when there's no data (caller doesn't have to
 * guard).
 */
export const StackBar = ({ segments, className }: StackBarProps) => {
  const total = segments.reduce((sum, s) => sum + s.minutes, 0);
  if (total === 0) return null;
  const visible = segments.filter((s) => s.minutes > 0);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        role="img"
        aria-label="Project mix"
        className={cn(
          'flex h-[14px] overflow-hidden rounded-full',
          'bg-[var(--surface-quiet,var(--secondary))]',
        )}
      >
        {visible.map((s) => {
          const pct = (s.minutes / total) * 100;
          return (
            <span
              key={s.id}
              title={`${s.label}: ${fmtHours(s.minutes)} (${pct.toFixed(0)}%)`}
              className="h-full transition-[filter] duration-100 hover:brightness-110"
              style={{ width: `${pct}%`, background: s.color }}
            />
          );
        })}
      </div>
      <div className="text-foreground-muted flex flex-wrap gap-x-3.5 gap-y-1.5 text-[11px]">
        {visible.map((s) => {
          const pct = (s.minutes / total) * 100;
          return (
            <span key={s.id} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="size-2 flex-shrink-0 rounded-[2.5px]"
                style={{ background: s.color }}
              />
              <span className="truncate">{s.label}</span>
              <span className="text-foreground-faint ml-1 font-mono text-[10.5px] tabular-nums">
                {fmtHours(s.minutes)} · {pct.toFixed(0)}%
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
};
